import type {
  Correcao,
  CriarCorrecaoPayload,
  MensagemCorrecao,
  ResolverCorrecaoPayload,
  TrabalhoLock,
} from "@/interfaces/services/correcao";
import {
  removerMarcacoesDeTela,
  semIdsProvisorios,
} from "@/utils/marcacoesDeTela";
import api from "./api";

export type CriarCorrecaoResponse =
  | Correcao
  | { ignored: boolean; reason: string };
export type AtualizarCorrecaoResponse =
  | Correcao
  | { deleted: true; uuid: string; reason: string };

export interface ListarCorrecoesResponse {
  data: Correcao[] | null;
  etag?: string;
  lastModified?: string;
  fromCache: boolean;
}

export async function listarCorrecoes(trabalhoId: number, options?: {
  etag?: string;
  lastModified?: string;
}) {
  const headers: Record<string, string> = {};

  // Add conditional headers for cache validation
  if (options?.etag) {
    headers['If-None-Match'] = options.etag;
  }
  if (options?.lastModified) {
    headers['If-Modified-Since'] = options.lastModified;
  }

  try {
    const response = await api.get<Correcao[]>(
      `/trabalhos/${trabalhoId}/correcoes`,
      { headers }
    );

    return {
      data: response.data,
      etag: response.headers['etag'],
      lastModified: response.headers['last-modified'],
      fromCache: false
    };
  } catch (error: any) {
    // Handle 304 Not Modified response
    if (error.response?.status === 304) {
      return {
        data: null, // Indicates data hasn't changed
        etag: options?.etag,
        lastModified: options?.lastModified,
        fromCache: true
      };
    }
    throw error;
  }
}

// O que só existe na tela do editor não vai para o banco: o id provisório dos
// títulos muda de dono ao reabrir e fazia a correção não se achar — ou se achar
// no título errado. Ver `utils/marcacoesDeTela.ts`.
export async function criarCorrecao(
  payload: CriarCorrecaoPayload
): Promise<CriarCorrecaoResponse> {
  const { data } = await api.post<CriarCorrecaoResponse>(`/correcoes`, {
    ...payload,
    ...(payload.element_ids
      ? { element_ids: semIdsProvisorios(payload.element_ids) }
      : {}),
    html_original: removerMarcacoesDeTela(payload.html_original),
    html_proposto: removerMarcacoesDeTela(payload.html_proposto),
  });
  return data;
}

export async function resolverCorrecao(
  correcaoUuid: string,
  status: ResolverCorrecaoPayload["status"]
) {
  const { data } = await api.put<Correcao>(
    `/correcoes/${correcaoUuid}/resolver`,
    { status }
  );
  return data;
}

export async function atualizarCorrecao(
  correcaoUuid: string,
  html_proposto: string
): Promise<AtualizarCorrecaoResponse> {
  const { data } = await api.put<AtualizarCorrecaoResponse>(
    `/correcoes/${correcaoUuid}`,
    { html_proposto: removerMarcacoesDeTela(html_proposto) }
  );
  return data;
}

export async function excluirCorrecao(correcaoUuid: string) {
  await api.delete(`/correcoes/${correcaoUuid}`);
  return true;
}

export async function listarMensagens(correcaoUuid: string) {
  const { data } = await api.get<MensagemCorrecao[]>(
    `/correcoes/${correcaoUuid}/mensagens`
  );
  return data;
}

export async function enviarMensagem(correcaoUuid: string, mensagem: string) {
  const { data } = await api.post<MensagemCorrecao>(
    `/correcoes/${correcaoUuid}/mensagens`,
    { mensagem }
  );
  return data;
}

// Locks
export async function obterLock(trabalhoId: number) {
  const { data } = await api.get<TrabalhoLock | null>(
    `/trabalhos/${trabalhoId}/lock`
  );
  return data;
}

export async function adquirirLock(trabalhoId: number) {
  const { data } = await api.post<TrabalhoLock>(
    `/trabalhos/${trabalhoId}/lock/acquire`
  );
  return data;
}

export async function liberarLock(
  trabalhoId: number,
  { aoFecharPagina = false }: { aoFecharPagina?: boolean } = {}
) {
  const { data } = await api.post<{ message: string }>(
    `/trabalhos/${trabalhoId}/lock/release`,
    undefined,
    // Com a aba fechando, só o fetch com keepalive garante que a requisição
    // sai antes de a página morrer; o XHR padrão do axios é cancelado.
    aoFecharPagina
      ? { adapter: "fetch", fetchOptions: { keepalive: true } }
      : undefined
  );
  return data;
}

export async function heartbeatLock(trabalhoId: number) {
  const { data } = await api.post<TrabalhoLock>(
    `/trabalhos/${trabalhoId}/lock/heartbeat`
  );
  return data;
}
